import React, { useState, useRef } from 'react';
import './LoginForm.css';
import { 
  signInWithEmailAndPassword, 
  signInWithPhoneNumber,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  setPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, functions } from '../firebase';
import { startSession, markLoginAttempt, clearLoginAttempt } from '../utils/sessionTimeout';
import PhoneInput, { formatE164ForDisplay } from './PhoneInput';
// Security logging temporarily disabled to avoid App Check conflicts during login
// import { logLoginSuccess, logLoginFailed, logAccountLockout } from '../utils/securityLogger';
// Email 2FA removed for immediate email login. The old src/utils/email2FA.js
// module was deleted (security review, 2026-07-14) — it returned the OTP
// code to the client and alert()'d it in plaintext, a complete 2FA bypass if
// ever re-wired. If email 2FA is reintroduced, model it on the hardened
// sendEmailOTP/verifyEmailOTP Cloud Functions (crypto-random code, hashed at
// rest, rate-limited) rather than resurrecting this file.

function LoginForm({ onLoginSuccess, logoutReason, onLoginAttemptStart }) {
  const [loginMethod, setLoginMethod] = useState('phone'); // 'phone' or 'email'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // SMS Verification state
  const [verificationCode, setVerificationCode] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);

  // 6-digit PIN input state
  const [pinDigits, setPinDigits] = useState(['', '', '', '', '', '']);
  const pinRefs = useRef([]);
  
  // Rate limiting state
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);

  // Initialize reCAPTCHA verifier (required for SMS verification)
  const setupRecaptcha = () => {
    // Clean up old verifier if it exists
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch (error) {
        console.log('Error clearing reCAPTCHA:', error);
      }
      window.recaptchaVerifier = null;
    }
    
    // Create fresh verifier (force render to avoid stale instances)
    window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
      size: 'invisible',
      callback: () => {},
      'expired-callback': () => {
        setError('reCAPTCHA expired. Please try again.');
        window.recaptchaVerifier = null; // Reset on expiration
      }
    });

    window.recaptchaVerifier.render().catch((err) => {
      console.error('Failed to render reCAPTCHA:', err);
      setError('Unable to load reCAPTCHA. Please reload the page.');
      window.recaptchaVerifier = null;
    });
  };

  // Step 1: Send SMS verification code
  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    onLoginAttemptStart?.();

    // Check rate limiting
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const remainingSeconds = Math.ceil((lockoutUntil - Date.now()) / 1000);
      setError(`Too many failed attempts. Please wait ${remainingSeconds} seconds.`);
      return;
    }

    // Format phone number to international format
    let formattedPhone = phoneNumber.trim();
    
    // If phone doesn't start with +, add +65 (Singapore default)
    if (!formattedPhone.startsWith('+')) {
      formattedPhone = '+65' + formattedPhone;
    }

    // Validate phone number has at least country code + 6 digits
    const digitsOnly = formattedPhone.replace(/\D/g, '');
    if (digitsOnly.length < 8) {
      setError('Please enter a valid phone number.');
      return;
    }

    setLoading(true);
    try {
      // Check if this phone number belongs to a registered account before sending OTP.
      // This prevents SMS costs for unknown numbers and gives clear feedback.
      try {
        const checkPhoneExists = httpsCallable(functions, 'checkPhoneExists');
        const checkResult = await checkPhoneExists({ phoneNumber: formattedPhone });
        if (!checkResult.data?.exists) {
          setError('No account found for this number. Please contact your administrator.');
          return;
        }
      } catch (checkErr) {
        // If the pre-check function is unavailable (e.g. not yet deployed), proceed anyway
        console.warn('Phone pre-check unavailable, proceeding with OTP:', checkErr);
      }

      setupRecaptcha();
      const appVerifier = window.recaptchaVerifier;
      await setPersistence(auth, browserSessionPersistence);
      const confirmation = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
      
      setConfirmationResult(confirmation);
      setShowCodeInput(true);
      resetPin();
      setError('');
      setFailedAttempts(0);
    } catch (err) {
      console.error('Error sending code:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      
      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);
      
      if (newFailedAttempts >= 5) {
        const lockoutTime = Date.now() + (15 * 60 * 1000);
        setLockoutUntil(lockoutTime);
        setError('Too many failed attempts. Account locked for 15 minutes.');
      } else {
        if (err.code === 'auth/invalid-phone-number') {
          setError('Invalid phone number format.');
        } else if (err.code === 'auth/too-many-requests') {
          setError('Too many requests. Please try again later.');
        } else if (err.code === 'auth/operation-not-allowed') {
          setError('Phone authentication is not enabled. Please enable it in Firebase Console.');
        } else if (err.code === 'auth/invalid-app-credential') {
          setError('Firebase configuration error. Please check API settings.');
        } else {
          setError(`Failed to send verification code. Error: ${err.code || 'Unknown'} (${newFailedAttempts}/5 attempts)`);
        }
      }
      window.recaptchaVerifier = null; // Reset verifier on error
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify SMS code and complete login
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);
    try {
      // Must be set before the credential call — App.js's onAuthStateChanged
      // handler runs before confirm() resolves. See markLoginAttempt().
      markLoginAttempt();
      const result = await confirmationResult.confirm(verificationCode);
      const user = result.user;

      // Format phone number
      let formattedPhone = phoneNumber.trim();
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = '+65' + formattedPhone;
      }

      // Read the authenticated user's Firestore profile.
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      let userName = 'Worker';
      let userRole = 'worker';

      if (!userDocSnap.exists()) {
        // No account found — this phone number was not pre-registered by an admin.
        // Sign out immediately and show a clear error.
        await auth.signOut();
        setError('No account found. Please contact your administrator.');
        setShowCodeInput(false);
        setVerificationCode('');
        return;
      }

      const userData = userDocSnap.data();
      userName = userData.name || 'Worker';

      // Get role from Firebase Custom Claims (secure, server-side)
      const token = await user.getIdTokenResult();
      userRole = token.claims.role || userData.role || 'worker';

      // Update last login timestamp
      await setDoc(
        userDocRef,
        {
          phoneNumber: formattedPhone,
          lastLogin: serverTimestamp(),
          authMethod: 'phone'
        },
        { merge: true }
      );

      // Start the idle/absolute session timers for this fresh login (see
      // src/utils/sessionTimeout.js) and cache basic profile info.
      startSession();
      try {
        sessionStorage.setItem('user', JSON.stringify({
          uid: user.uid,
          phoneNumber: formattedPhone,
          name: userName,
        }));
      } catch (storageErr) {
        console.warn("Unable to store user info in localStorage:", storageErr);
      }

      // Security logging disabled to avoid App Check conflicts
      // await logLoginSuccess(user, 'phone');
      
      onLoginSuccess(userName, userRole, formattedPhone);
      setError('');
      setPhoneNumber('');
      resetPin();
      setShowCodeInput(false);
      setFailedAttempts(0);
      setLockoutUntil(null);
    } catch (err) {
      console.error('Error verifying code:', err);
      clearLoginAttempt();

      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);
      
      // Security logging disabled to avoid App Check conflicts
      // let formattedPhone = phoneNumber.trim();
      // if (!formattedPhone.startsWith('+')) {
      //   formattedPhone = '+65' + formattedPhone;
      // }
      // await logLoginFailed(formattedPhone, err.code || 'verification_failed', 'phone');
      
      if (newFailedAttempts >= 5) {
        const lockoutTime = Date.now() + (15 * 60 * 1000);
        setLockoutUntil(lockoutTime);
        setError('Too many failed attempts. Account locked for 15 minutes.');
        
        // Security logging disabled
        // await logAccountLockout(formattedPhone, '15 minutes');
      } else {
        if (err.code === 'auth/invalid-verification-code') {
          setError(`Invalid verification code. (${newFailedAttempts}/5 attempts)`);
        } else if (err.code === 'auth/code-expired') {
          setError('Verification code expired. Please request a new one.');
          setShowCodeInput(false);
          resetPin();
        } else {
          setError(`Verification failed. Please try again. (${newFailedAttempts}/5 attempts)`);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    onLoginAttemptStart?.();

    // Check rate limiting
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const remainingSeconds = Math.ceil((lockoutUntil - Date.now()) / 1000);
      setError(`Too many failed attempts. Please wait ${remainingSeconds} seconds.`);
      return;
    }

    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();

    if (!trimmedEmail || !trimmedPassword) {
      setError('Please fill in all fields.');
      return;
    }

    setLoading(true);
    try {
      // OPTIMIZED: Only authenticate with Firebase, don't read Firestore during login
      // This makes login instant (<1 second) instead of waiting for App Check + Firestore
      await setPersistence(auth, browserSessionPersistence);
      // Must be set before the credential call — App.js's onAuthStateChanged
      // handler runs before signInWithEmailAndPassword() resolves. See
      // markLoginAttempt().
      markLoginAttempt();
      const userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, trimmedPassword);
      const user = userCredential.user;

      // Get role from Firebase Custom Claims (secure, server-side, fast)
      const token = await user.getIdTokenResult();
      const userRole = token.claims.role || 'worker';

      // Start the idle/absolute session timers for this fresh login (see
      // src/utils/sessionTimeout.js). App.js will load full profile from
      // Firestore in background after login.
      startSession();
      try {
        sessionStorage.setItem('user', JSON.stringify({
          uid: user.uid,
          email: trimmedEmail,
          // Full profile (name, phone) will be loaded by App.js from Firestore
        }));
      } catch (storageErr) {
        console.warn("Unable to store user info in sessionStorage:", storageErr);
      }

      // Security logging disabled to avoid App Check conflicts during login
      // Logging will be handled in App.js after successful authentication
      
      // Call success callback with basic info
      // App.js will load full user profile from Firestore in background
      onLoginSuccess('User', userRole, trimmedEmail);
      setError('');
      setFailedAttempts(0); // Reset on successful login
      setLockoutUntil(null);
    } catch (err) {
      console.error('Auth error:', err);
      clearLoginAttempt();

      // Increment failed attempts
      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);
      
      // Security logging disabled to avoid App Check conflicts
      // await logLoginFailed(trimmedEmail, err.code || 'auth_failed', 'email');
      
      // Lockout after 5 failed attempts
      if (newFailedAttempts >= 5) {
        const lockoutTime = Date.now() + (15 * 60 * 1000); // 15 minutes
        setLockoutUntil(lockoutTime);
        setError('Too many failed attempts. Account locked for 15 minutes.');
        
        // Security logging disabled
        // await logAccountLockout(trimmedEmail, '15 minutes');
      } else {
        if (
          err.code === 'auth/user-not-found' ||
          err.code === 'auth/wrong-password' ||
          err.code === 'auth/invalid-credential'
        ) {
          setError(`Email/password is incorrect. (${newFailedAttempts}/5 attempts)`);
        } else {
          setError('Something went wrong. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    const targetEmail = (email || '').trim().toLowerCase() || window.prompt('Enter your email to reset password:')?.trim().toLowerCase();
    if (!targetEmail) return;
    try {
      await sendPasswordResetEmail(auth, targetEmail);
      alert(`Password reset email sent to ${targetEmail}. Check your inbox/spam.`);
    } catch (err) {
      console.error('Error sending password reset email:', err);
      setError('Unable to send reset email. Please check the email and try again.');
    }
  };


  // PIN input handlers
  const handlePinChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...pinDigits];
    newDigits[index] = digit;
    setPinDigits(newDigits);
    setVerificationCode(newDigits.join(''));
    if (digit && index < 5) {
      pinRefs.current[index + 1]?.focus();
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      pinRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      pinRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      pinRefs.current[index + 1]?.focus();
    }
  };

  const handlePinPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newDigits = Array.from({ length: 6 }, (_, i) => pasted[i] || '');
    setPinDigits(newDigits);
    setVerificationCode(newDigits.join(''));
    const nextEmpty = newDigits.findIndex(d => !d);
    pinRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
  };

  const resetPin = () => {
    setPinDigits(['', '', '', '', '', '']);
    setVerificationCode('');
  };

  return (
    <div className="login-container">
      <div id="recaptcha-container"></div>
      <div className="login-box">
        <h2>Login</h2>

        {logoutReason && (
          <div
            className="login-note"
            style={{
              marginBottom: '16px',
              padding: '10px',
              borderRadius: '4px',
              background: '#fffbeb',
              color: '#92400e',
              border: '1px solid #fde68a',
              fontSize: '14px',
              textAlign: 'center'
            }}
          >
            {logoutReason === 'inactivity'
              ? 'You were logged out due to inactivity. Please log in again.'
              : 'Your session expired. Please log in again.'}
          </div>
        )}

        {/* Login Method Toggle */}
        <div className="login-method-toggle" style={{ marginBottom: '20px' }}>
          <button
            type="button"
            className={loginMethod === 'phone' ? 'is-active' : ''}
            onClick={() => {
              setLoginMethod('phone');
              setPhoneNumber('');
              setPassword('');
              resetPin();
              setShowCodeInput(false);
              setError('');
            }}
          >
            📱 Phone
          </button>
          <button
            type="button"
            className={loginMethod === 'email' ? 'is-active' : ''}
            onClick={() => {
              setLoginMethod('email');
              setEmail('');
              setPassword('');
              setError('');
            }}
          >
            ✉️ Email
          </button>
        </div>

        {loginMethod === 'phone' ? (
          !showCodeInput ? (
            /* Step 1: Enter phone number and send code */
            <form onSubmit={handleSendCode}>
              <PhoneInput
                value={phoneNumber}
                onChange={(e) => { setPhoneNumber(e.target.value); if (error) setError(''); }}
                disabled={loading}
                required
              />
              <button type="submit" disabled={loading} className="otp-verify-btn">
                {loading ? 'Sending…' : 'Send Code'}
              </button>
              <div className="login-note" style={{ marginTop: '12px' }}>
                You'll receive a 6-digit verification code via SMS
              </div>
            </form>
          ) : (
            /* Step 2: Enter verification code */
            <form onSubmit={handleVerifyCode}>
              <div style={{ 
                fontSize: '14px', 
                color: '#666', 
                marginBottom: '20px', 
                textAlign: 'center',
                padding: '10px',
                background: '#f0f9ff',
                borderRadius: '4px'
              }}>
                Verification code sent to<br />
                <strong>{formatE164ForDisplay(phoneNumber)}</strong>
              </div>

              <div className="pin-input-row">
                {pinDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => pinRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    onChange={e => handlePinChange(i, e.target.value)}
                    onKeyDown={e => handlePinKeyDown(i, e)}
                    onPaste={handlePinPaste}
                    disabled={loading}
                    className="pin-box"
                    autoFocus={i === 0}
                    autoComplete="off"
                  />
                ))}
              </div>

              <button type="submit" disabled={loading || verificationCode.length < 6} className="otp-verify-btn">
                {loading ? 'Verifying…' : 'Log In'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowCodeInput(false);
                  resetPin();
                  setError('');
                }}
                disabled={loading}
                className="use-different-link"
              >
                Use a different number
              </button>
            </form>
          )
        ) : (
          /* Email Login Form */
          <form onSubmit={handleEmailLogin}>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />

            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />

            <button type="submit" disabled={loading} className="otp-verify-btn">
              {loading ? 'Logging in…' : 'Log In'}
            </button>

            <div style={{ marginTop: '8px', textAlign: 'center' }}>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); if (!loading) handleForgotPassword(); }}
                className="forgot-link"
                aria-disabled={loading}
              >
                Forgot password?
              </a>
            </div>
          </form>
        )}

        {loginMethod === 'email' && (
          <div className="login-note" style={{ marginTop: '15px' }}>
            Need access or forgot your password? Please contact your administrator.
          </div>
        )}

        {error && <div className="error-message" style={{ marginTop: '15px' }}>{error}</div>}
      </div>
    </div>
  );
}

export default LoginForm;
